import axios from 'axios';
import { getAccessToken, isAccessTokenExpired, setAccessToken } from './tokenManager';
import { getRefreshToken, removeRefreshToken, setRefreshToken } from '../utils/authCookies';
import { API_ENDPOINTS } from './endpoints';

const BASE_URL = 'https://dev-api.ufuqmiayar.com/api/v1';

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // for refresh-token cookie
  //   headers: {
  //     'Content-Type': 'application/json',
  //   },
});

const refreshClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

export interface RefreshedSession {
  accessToken: string;
  accessTokenExpiresAt?: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
  username: string;
  authorities: string[];
}

let refreshRequest: Promise<RefreshedSession | null> | null = null;

const setAuthorizationHeader = (headers: any, token: string) => {
  if (typeof headers?.set === 'function') {
    headers.set('Authorization', `Bearer ${token}`);
    return;
  }

  headers.Authorization = `Bearer ${token}`;
};

export const refreshAccessToken = async (): Promise<RefreshedSession | null> => {
  if (refreshRequest) return refreshRequest;

  refreshRequest = (async () => {
    const storedRefreshToken = getRefreshToken();

    if (!storedRefreshToken) return null;

    try {
      const response = await refreshClient.post(API_ENDPOINTS.REFRESH, {
        refreshToken: storedRefreshToken,
      });

      const data = response.data as RefreshedSession;

      setAccessToken(data.accessToken, data.accessTokenExpiresAt);

      if (data.refreshToken) {
        setRefreshToken(data.refreshToken, data.refreshTokenExpiresAt);
      }

      return data;
    } catch (error) {
      setAccessToken(null);
      removeRefreshToken();

      throw error;
    } finally {
      refreshRequest = null;
    }
  })();

  return refreshRequest;
};

apiClient.interceptors.request.use(
  async (config) => {
    let token = getAccessToken();

    const publicRoutes = [API_ENDPOINTS.LOGIN, API_ENDPOINTS.REFRESH];

    const isPublicRoute = publicRoutes.some((route) => config.url?.includes(route));

    if (!isPublicRoute && (!token || isAccessTokenExpired()) && getRefreshToken()) {
      token = (await refreshAccessToken())?.accessToken ?? null;
    }

    if (token && !isPublicRoute) {
      setAuthorizationHeader(config.headers, token);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isUnauthorized = error.response?.status === 401;
    const isRefreshRoute = originalRequest?.url?.includes(API_ENDPOINTS.REFRESH);

    if (!isUnauthorized || originalRequest?._retry || isRefreshRoute) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const token = (await refreshAccessToken())?.accessToken;

      if (token) {
        originalRequest.headers = originalRequest.headers || {};
        setAuthorizationHeader(originalRequest.headers, token);
        return apiClient(originalRequest);
      }

      return Promise.reject(error);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  },
);

export default apiClient;
